import Papa from 'papaparse'

/** An uploaded CSV as rows of cells, including the header row and any all-empty rows. */
export type CsvRows = string[][]

const UTF8_BOM = [0xef, 0xbb, 0xbf]

const readBytes = (file: Blob): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer))
    reader.onerror = () =>
      reject(reader.error ?? new Error('The file could not be read.'))
    reader.readAsArrayBuffer(file)
  })

const decodeUtf8 = (bytes: Uint8Array): string | null => {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    return null
  }
}

const decodeWindows1252 = (bytes: Uint8Array): string =>
  new TextDecoder('windows-1252').decode(bytes)

let windows1252Bytes: Map<string, number> | null = null

/**
 * Windows-1252 decodes every byte to a character of its own, so this reverses
 * it exactly: a cell read that way can be turned back into the bytes the file
 * held.
 */
const windows1252ByteOf = (): Map<string, number> => {
  if (windows1252Bytes === null) {
    const characters = decodeWindows1252(
      Uint8Array.from({ length: 256 }, (_, byte) => byte)
    )
    windows1252Bytes = new Map(
      Array.from(characters, (character, byte) => [character, byte])
    )
  }

  return windows1252Bytes
}

const isAscii = (text: string): boolean => {
  for (let i = 0; i < text.length; i++) {
    if (text.charCodeAt(i) > 0x7f) {
      return false
    }
  }

  return true
}

/** One cell, the way Utf8Sanitizer decodes it: UTF-8 when valid, Windows-1252 otherwise. */
const decodeCell = (cellAs1252: string): string => {
  // ASCII reads the same in both encodings.
  if (isAscii(cellAs1252)) {
    return cellAs1252
  }

  const byteOf = windows1252ByteOf()
  const bytes: number[] = []
  for (const character of cellAs1252) {
    const byte = byteOf.get(character)
    if (byte === undefined) {
      return cellAs1252
    }
    bytes.push(byte)
  }

  return decodeUtf8(Uint8Array.from(bytes)) ?? cellAs1252
}

// Comma only, as PHP's fgetcsv reads it: Papa would otherwise guess a delimiter.
const parse = (text: string): CsvRows =>
  Papa.parse<string[]>(text, { delimiter: ',', skipEmptyLines: true }).data

/**
 * Decodes the file the way the import endpoints do.
 *
 * The server decodes each cell on its own (Utf8Sanitizer): UTF-8 when the cell is
 * valid UTF-8, otherwise Windows-1252. A file that is UTF-8 throughout decodes in
 * one pass. Any other file is parsed through Windows-1252, which keeps each
 * cell's bytes recoverable, and every cell is then decoded like the server does
 * it. A file mixing the two encodings therefore keeps both kinds of accents.
 */
export const decodeCsvBytes = (bytes: Uint8Array): CsvRows => {
  const startsWithBom = UTF8_BOM.every((byte, i) => bytes[i] === byte)
  const body = startsWithBom ? bytes.subarray(UTF8_BOM.length) : bytes

  const utf8 = decodeUtf8(body)
  if (utf8 !== null) {
    return parse(utf8)
  }

  return parse(decodeWindows1252(body)).map((row) => row.map(decodeCell))
}

/**
 * Reads and decodes an uploaded CSV once. The duplicate review reads these rows
 * for its row index, labels, name check and rewrite, so all four agree.
 */
export const decodeCsvFile = async (file: Blob): Promise<CsvRows> =>
  decodeCsvBytes(await readBytes(file))
